import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LazyLoadEvent } from 'primeng/api';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';
import { Global } from '../../global';
import { SeguridadService } from './seguridad.service';
import { GrupoAccesoRegistro } from '../models/seguridad/grupo_acceso.model';

@Injectable({
    providedIn: 'root',
})
export class BaseService {
    seguridadService = new SeguridadService();

    /** objeto que selecionado */
    obj: Record<string, any>;

    dtPrefix = Global.PREFIX_RECORD;
    /**
     * nombre de la tabla en la base de datos
     */
    recordName!: string;
    /**
     * codigo del registro
     */
    codigoRegistro!: string;
    /**
     * codigo del modulo
     */
    codigoModulo: string;
    /**
     * Url correspondiente al servicio a consultar.
     */
    url!: string;
    /**
     * Cuerpo de la consulta a ser realizada.
     */
    body: any = {};

    header: Record<string, any> = {
        jwt_api: environment.JWT_API,
        'x-token': this.seguridadService.getToken(),
        controlar: 'true',
        version: environment.VERSION,
    };

    constructor(public http: HttpClient) {}

    async esAutogestion() {
        const cu = this.seguridadService.getCurrentUser();
        if (cu?.empresa?.autogestion) {
            return true;
        }
        return false;
    }

    async updateToken() {
        const token = this.seguridadService.getToken();
        this.header['x-token'] = token;
    }

    async get(id: number): Promise<ServiceResponse> {
        try {
            await this.updateToken();
            this.header['filters'] = JSON.stringify(
                await this.getAdicionalFilters()
            );
            let url = `${this.url}/${id}`;
            const resp = await lastValueFrom(
                this.http.get<any>(url, { headers: this.header })
            );

            return resp;
        } catch (error: any) {
            return this.checkError(error);
        }
    }

    async getAll(
        size?: number,
        otherFilters?: Record<string, any>
    ): Promise<ServiceResponse> {
        try {
            await this.updateToken();
            this.obj = {};
            const params = await this.getParams();
            const filters = await this.getFilters();
            if (otherFilters) {
                filters.filters = JSON.stringify(otherFilters.filters);
            }
            if (size) {
                params.size = size;
            }

            this.header['filters'] = JSON.stringify(filters);

            const resp = await lastValueFrom(
                this.http.get<any>(this.url, { headers: this.header, params })
            );
            return resp;
        } catch (error: any) {
            console.log(error);

            return this.checkError(error);
        }
    }

    async post(body: any): Promise<ServiceResponse> {
        try {
            await this.updateToken();
            this.header['filters'] = JSON.stringify(
                await this.getAdicionalFilters()
            );
            this.body = body;
            delete this.body.id;
            let url = `${this.url}`;
            const resp = await lastValueFrom(
                this.http.post<any>(`${url}`, this.body, {
                    headers: this.header,
                })
            );
            return resp;
        } catch (error: any) {
            console.log(error);

            return this.checkError(error);
        }
    }

    async postAll(body: any): Promise<ServiceResponse> {
        try {
            await this.updateToken();
            this.header['filters'] = JSON.stringify(
                await this.getAdicionalFilters()
            );
            this.body = body;
            let url = `${this.url}/create/bulk`;
            const resp = await lastValueFrom(
                this.http.post<any>(`${url}`, this.body, {
                    headers: this.header,
                })
            );
            return resp;
        } catch (error: any) {
            console.log(error);
            return this.checkError(error);
        }
    }

    async put(id: number, body: any): Promise<ServiceResponse> {
        try {
            await this.updateToken();
            this.header['filters'] = JSON.stringify(
                await this.getAdicionalFilters()
            );
            this.body = body;
            let url = `${this.url}/${id}`;
            const resp = await lastValueFrom(
                this.http.put<ServiceResponse>(url, this.body, {
                    headers: this.header,
                })
            );
            return resp;
        } catch (error: any) {
            console.log(error);

            return this.checkError(error);
        }
    }

    async delete(id: number): Promise<ServiceResponse> {
        try {
            await this.updateToken();
            this.header['filters'] = JSON.stringify(
                await this.getAdicionalFilters()
            );
            let url = `${this.url}/${id}`;
            const resp = await lastValueFrom(
                this.http.delete<ServiceResponse>(url, { headers: this.header })
            );
            return resp;
        } catch (error: any) {
            return this.checkError(error);
        }
    }

    async getParams(): Promise<Record<string, any>> {
        const dt = sessionStorage.getItem(
            `${this.dtPrefix}.${this.recordName}`
        );

        if (dt) {
            const dtJson = JSON.parse(dt!) as LazyLoadEvent;
            let { rows, first } = dtJson;
            if (!rows) {
                rows = 10;
            }
            let page = 0;
            if (first !== 0) {
                page = Math.ceil(first! / rows!);
            }
            return { page, size: rows };
        }
        return { page: 0, size: 10 };
    }

    async getFilters(): Promise<Record<string, any>> {
        const dt = sessionStorage.getItem(
            `${this.dtPrefix}.${this.recordName}1`
        );
        const dtJson = JSON.parse(dt!) as LazyLoadEvent;
        const multiSortMeta = dtJson?.multiSortMeta ? dtJson.multiSortMeta : [];
        const globalFilter = dtJson?.globalFilter;
        const fields = sessionStorage.getItem(
            `${this.dtPrefix}.${this.recordName}_fields`
        );
        const filters = fields;
        const aditionalFilters = JSON.stringify([
            ...(await this.getAdicionalFilters()),
            await this.getFiltroAdmin(),
        ]);
        const groupBy: [] = [];
        return {
            filters,
            aditionalFilters,
            multiSortMeta,
            globalFilter,
            groupBy,
        };
    }

    async getFiltroAdmin(): Promise<Record<string, any>> {
        const cu = this.seguridadService.getCurrentUser();
        if (!cu.super) {
            const empresa = cu?.empresa;
            if (empresa?.autogestion && empresa.id !== empresa.id_admin) {
                return {
                    field: 'empresa.id_admin',
                    value: cu.empresa.id,
                    type: 'ref',
                    refName: 'Empresa',
                };
            }
        }
        return {};
    }

    async getAdicionalFilters(
        filtrarEmpresa: boolean = true
    ): Promise<Record<string, any>[]> {
        const currentUser = this.seguridadService.getCurrentUser();
        let filtros: Record<string, any>[] = [];

        if (!this.codigoModulo && !this.codigoRegistro) {
            return filtros;
        }

        const acceso = await this.seguridadService.tipoAcceso(
            this.codigoModulo,
            this.codigoRegistro
        );
        if (acceso === GrupoAccesoRegistro.ACCESO_TODO) {
            if (this.obj?.id_empresa) {
                filtros.push({
                    field: 'id_empresa',
                    type: 'id',
                    value: this.obj.id_empresa,
                });
            }
            return filtros;
        }

        if (acceso === GrupoAccesoRegistro.ACCESO_EMPRESA) {
            if (
                this.codigoRegistro ===
                Global.MENU.administracion.registros.empresa.codigo
            ) {
                filtros = [
                    { field: 'id', type: 'id', value: currentUser?.id_empresa },
                ];
            } else {
                if (filtrarEmpresa) {
                    filtros = [
                        {
                            field: 'id_empresa',
                            type: 'id',
                            value: currentUser?.id_empresa,
                        },
                    ];
                }
            }
            return filtros;
        }

        if (acceso === GrupoAccesoRegistro.ACCESO_SUCURSAL) {
            if (
                this.codigoRegistro ===
                Global.MENU.administracion.registros.empresa.codigo
            ) {
                filtros = [
                    { field: 'id', type: 'id', value: currentUser?.id_empresa },
                ];
            } else {
                if (filtrarEmpresa) {
                    filtros = [
                        {
                            field: 'id_empresa',
                            type: 'id',
                            value: currentUser?.id_empresa,
                        },
                    ];
                }
            }
            if (
                this.codigoRegistro ===
                Global.MENU.administracion.registros.sucursal.codigo
            ) {
                filtros.push({
                    field: 'id',
                    type: 'id',
                    value: currentUser?.id_sucursal,
                });
            } else {
                filtros.push({
                    field: 'id_sucursal',
                    type: 'id',
                    value: currentUser?.id_sucursal,
                });
            }
            return filtros;
        }

        if (acceso === GrupoAccesoRegistro.ACCESO_USUARIO) {
            if (
                this.codigoRegistro ===
                Global.MENU.administracion.registros.empresa.codigo
            ) {
                filtros = [
                    { field: 'id', type: 'id', value: currentUser?.id_empresa },
                ];
            } else {
                if (filtrarEmpresa) {
                    filtros = [
                        {
                            field: 'id_empresa',
                            type: 'id',
                            value: currentUser?.id_empresa,
                        },
                    ];
                }
            }

            if (
                this.codigoRegistro ===
                Global.MENU.administracion.registros.sucursal.codigo
            ) {
                filtros.push({
                    field: 'id',
                    type: 'id',
                    value: currentUser?.id_sucursal,
                });
            } else {
                filtros.push({
                    field: 'id_sucursal',
                    type: 'id',
                    value: currentUser?.id_sucursal,
                });
            }
            filtros.push({
                field: 'id_creado_por',
                type: 'id',
                value: currentUser.id,
            });
            return filtros;
        }
        return filtros;
    }

    async checkError(error: any) {
        if (error?.status === 0) {
            return {
                ok: false,
                msg: 'Ha ocurrido un error inesperado',
                resp: 'No se pudo conectar con el servidor',
            };
        }
        if (error?.status === 401) throw error;
        if (error?.error) {
            const serverError = error.error;
            const ret: ServiceResponse = {
                ok: false,
                msg: 'Ha ocurrido un error inesperado',
                resp: serverError,
            };
            return ret;
        }
        return {
            ok: false,
            msg: 'No se pudo conectar con el servidor',
            resp: 'Verifique su conexión a internet y vuelva a intentar',
        };
    }

    async postImage(
        id: string,
        tipo: string,
        file: any
    ): Promise<ServiceResponse> {
        try {
            await this.updateToken();
            this.header['filters'] = JSON.stringify(
                await this.getAdicionalFilters()
            );
            const formData = new FormData();
            formData.append('imagen', file, file.name);
            // let url = `${this.url}`;
            const url = `${environment.host}/file/${tipo}/${id}`;
            const resp = await lastValueFrom(
                this.http.post<any>(`${url}`, formData, {
                    headers: this.header,
                })
            );
            return resp;
        } catch (error: any) {
            return this.checkError(error);
        }
    }

    async obtenerImagen(urlImagen: Record<string, any>): Promise<Blob> {
        try {
            await this.updateToken();
            const url = `${environment.host}/file/obtener-imagen`;
            const resp = await lastValueFrom(
                this.http.post(
                    url,
                    { urlImagen },
                    { headers: this.header, responseType: 'blob' }
                )
            );
            return resp as Blob;
        } catch (error: any) {
            console.error('Error al obtener la imagen:', error);
            throw error;
        }
    }
}

export interface ServiceResponse {
    ok: boolean;
    msg: string;
    resp: any;
}
